/**
 * Página de Formulario de Miembro
 * Permite crear o editar una persona
 */

import { useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import toast from "react-hot-toast";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  Input,
  Select,
  InlineLoader,
} from "@/components/ui";
import { DatePickerField } from "@/components/ui/DatePicker";
import {
  ArrowLeft,
  User,
  Save,
  Loader2,
  AlertCircle,
  Heart,
} from "lucide-react";
import {
  usePerson,
  useCreatePerson,
  useUpdatePerson,
} from "@/hooks/usePersons";
import { useAuthStore } from "@/stores/authStore";
import { Timestamp } from "firebase/firestore";
import { Breadcrumbs } from "@/components/ui/Breadcrumbs";
import EpsSelect from "@/components/forms/EpsSelect";
import type { IdDocumentType, Gender, BloodType, MemberType } from "@/types";
import { COUNTRY_CODES } from "@/lib/countryCodes";

const personSchema = z.object({
  idDocumentType: z.enum(["CC", "TI", "RC", "CE", "PASAPORTE", "PPT"]),
  idDocumentNumber: z
    .string()
    .min(5, "El número de documento debe tener al menos 5 caracteres"),
  firstName: z.string().min(2, "El nombre debe tener al menos 2 caracteres"),
  lastName: z.string().min(2, "El apellido debe tener al menos 2 caracteres"),
  birthDate: z.string().min(1, "La fecha de nacimiento es requerida"),
  gender: z.enum(["male", "female"]),
  email: z.string().email("Email inválido").optional().or(z.literal("")),
  phoneCountryCode: z.string().default("+57"),
  phone: z.string().min(1, "El teléfono es requerido"),
  address: z.string().optional(),
  bloodType: z.enum(["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"]),
  healthInsurance: z.string().optional(),
  emergencyContactName: z
    .string()
    .min(1, "El nombre del contacto de emergencia es requerido"),
  emergencyContactRelationship: z.string().min(1, "La relación es requerida"),
  emergencyContactCountryCode: z.string().default("+57"),
  emergencyContactPhone: z
    .string()
    .min(1, "El teléfono del contacto de emergencia es requerido"),
  status: z.enum(["active", "inactive"]).default("active"),
});

type PersonForm = z.infer<typeof personSchema>;

const documentTypeLabels: Record<string, string> = {
  CC: "Cédula de Ciudadanía",
  TI: "Tarjeta de Identidad",
  RC: "Registro Civil",
  CE: "Cédula de Extranjería",
  PASAPORTE: "Pasaporte",
  PPT: "Permiso por Protección Temporal (PPT)",
};

const bloodTypes = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];

const relationships = [
  { value: "Padre", label: "Padre" },
  { value: "Madre", label: "Madre" },
  { value: "Abuelo/a", label: "Abuelo/a" },
  { value: "Hermano/a", label: "Hermano/a" },
  { value: "Tío/a", label: "Tío/a" },
  { value: "Tutor Legal", label: "Tutor Legal" },
  { value: "Esposo/a", label: "Esposo/a" },
  { value: "Otro", label: "Otro" },
];

export default function MemberFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();

  const isEditing = Boolean(id);
  const { data: person, isLoading: isLoadingPerson } = usePerson(id || "");

  const createMutation = useCreatePerson();
  const updateMutation = useUpdatePerson();

  const formatDateForInput = (
    timestamp: { toDate?: () => Date } | Date | null | undefined,
  ) => {
    if (!timestamp) return "";
    const date =
      typeof timestamp === "object" && "toDate" in timestamp && timestamp.toDate
        ? timestamp.toDate()
        : new Date(timestamp as unknown as string | number | Date);
    return date.toISOString().split("T")[0];
  };

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    control,
    formState: { errors, isSubmitting },
  } = useForm<PersonForm>({
    resolver: zodResolver(personSchema),
    mode: "onTouched",
    defaultValues: {
      idDocumentType: "TI",
      status: "active",
      gender: "male",
      bloodType: "O+",
      phoneCountryCode: "+57",
      emergencyContactCountryCode: "+57",
      emergencyContactRelationship: "Madre",
    },
  });

  const watchBirthDate = watch("birthDate");

  const derivedMemberType = useMemo(() => {
    if (!watchBirthDate) return "acampante";
    const age = Math.floor(
      (Date.now() - new Date(watchBirthDate).getTime()) /
        (365.25 * 24 * 60 * 60 * 1000),
    );
    return age >= 16 ? "lider" : "acampante";
  }, [watchBirthDate]);

  // Cargar datos cuando se edita
  useEffect(() => {
    if (person && isEditing) {
      // Extraer código de país del teléfono si existe
      const extractCountryCode = (
        phone: string | undefined,
      ): { code: string; number: string } => {
        if (!phone) return { code: "+57", number: "" };
        // Si tiene espacio, separar por el primer espacio
        const spaceMatch = phone.match(/^(\+\d{1,4})\s+(.+)$/);
        if (spaceMatch) return { code: spaceMatch[1], number: spaceMatch[2] };
        // Sin espacio: matchear contra códigos de país conocidos (más largos primero)
        if (phone.startsWith("+")) {
          const knownCodes = [
            "+593",
            "+55",
            "+54",
            "+56",
            "+58",
            "+52",
            "+51",
            "+34",
            "+57",
            "+1",
          ];
          for (const code of knownCodes) {
            if (phone.startsWith(code))
              return { code, number: phone.slice(code.length) };
          }
          const fallback = phone.match(/^(\+\d{1,3})(.*)$/);
          if (fallback) return { code: fallback[1], number: fallback[2] };
        }
        return { code: "+57", number: phone };
      };

      const phoneData = extractCountryCode(person.phone);
      const emergencyPhoneData = extractCountryCode(
        person.emergencyContact?.phone,
      );

      reset({
        idDocumentType: person.idDocumentType,
        idDocumentNumber: person.idDocumentNumber,
        firstName: person.firstName,
        lastName: person.lastName,
        birthDate: formatDateForInput(person.birthDate),
        gender: person.gender,
        email: person.email || "",
        phoneCountryCode: phoneData.code,
        phone: phoneData.number,
        address: person.address || "",
        bloodType: person.bloodType,
        healthInsurance: person.healthInsurance || "",
        emergencyContactName: person.emergencyContact?.name || "",
        emergencyContactRelationship:
          person.emergencyContact?.relationship || "Madre",
        emergencyContactCountryCode: emergencyPhoneData.code,
        emergencyContactPhone: emergencyPhoneData.number,
        status: person.status,
      });
    }
  }, [person, isEditing, reset]);

  const onSubmit = async (data: PersonForm) => {
    try {
      const fullName = `${data.firstName} ${data.lastName}`;

      // Combinar código de país con teléfonos
      const phoneWithCode = `${data.phoneCountryCode} ${data.phone}`;
      const emergencyPhoneWithCode = `${data.emergencyContactCountryCode} ${data.emergencyContactPhone}`;

      const emergencyContact = {
        name: data.emergencyContactName,
        relationship: data.emergencyContactRelationship,
        phone: emergencyPhoneWithCode,
      };

      const personData = {
        idDocumentType: data.idDocumentType as IdDocumentType,
        idDocumentNumber: data.idDocumentNumber,
        firstName: data.firstName,
        lastName: data.lastName,
        fullName,
        birthDate: Timestamp.fromDate(new Date(data.birthDate + "T12:00:00")),
        gender: data.gender as Gender,
        memberType: derivedMemberType as MemberType,
        email: data.email || undefined,
        phone: phoneWithCode,
        address: data.address || undefined,
        bloodType: data.bloodType as BloodType,
        healthInsurance: data.healthInsurance || undefined,
        emergencyContact,
        status: data.status,
        photoUrl: undefined,
      };

      if (isEditing && id) {
        await updateMutation.mutateAsync({
          id,
          data: {
            ...personData,
            updatedAt: Timestamp.now(),
            updatedBy: user?.uid,
          },
        });
      } else {
        await createMutation.mutateAsync({
          ...personData,
          createdAt: Timestamp.now(),
          createdBy: user?.uid,
          updatedAt: Timestamp.now(),
        } as any);
      }

      navigate("/members");
    } catch (error) {
      console.error("Error saving person:", error);
      toast.error(
        error instanceof Error
          ? `Error al guardar: ${error.message}`
          : "No se pudo guardar. Intenta de nuevo.",
      );
    }
  };

  // Loading state para edición
  if (isEditing && isLoadingPerson) {
    return (
      <div className="flex items-center justify-center h-64">
        <InlineLoader size="lg" label="Cargando miembro…" />
      </div>
    );
  }

  // Error si se edita pero no existe
  if (isEditing && !isLoadingPerson && !person) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <AlertCircle className="w-12 h-12 text-red-400 mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Persona no encontrada
        </h2>
        <p className="text-gray-500 mb-4">
          No se pudo cargar la información de esta persona. Puede que se haya
          eliminado o que haya un problema de conexión.
        </p>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => window.location.reload()}>
            Reintentar
          </Button>
          <Button onClick={() => navigate("/members")}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver a miembros
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Breadcrumbs
        items={[
          { label: "Miembros", to: "/members" },
          {
            label: isEditing ? (person?.fullName ?? "Editar") : "Nueva persona",
          },
        ]}
      />
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="secondary"
          onClick={() => navigate("/members")}
          className="!p-2"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <User className="w-7 h-7 text-primary-600" />
            {isEditing ? "Editar Persona" : "Nueva Persona"}
          </h1>
          <p className="text-gray-500 mt-1">
            {isEditing
              ? `Actualizando: ${person?.fullName}`
              : "Registra una nueva persona en el sistema"}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Datos Personales */}
        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5 text-primary-600" />
              Datos Personales
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tipo de Documento *
                </label>
                <Select
                  {...register("idDocumentType")}
                  error={errors.idDocumentType?.message}
                >
                  {Object.entries(documentTypeLabels).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </Select>
              </div>
              <div>
                <Input
                  label="Número de Documento *"
                  {...register("idDocumentNumber")}
                  error={errors.idDocumentNumber?.message}
                  placeholder="1234567890"
                />
              </div>
              <div>
                <Input
                  label="Nombres *"
                  {...register("firstName")}
                  error={errors.firstName?.message}
                  placeholder="Juan Carlos"
                />
              </div>
              <div>
                <Input
                  label="Apellidos *"
                  {...register("lastName")}
                  error={errors.lastName?.message}
                  placeholder="Pérez García"
                />
              </div>
              <div>
                <DatePickerField
                  control={control}
                  name="birthDate"
                  label="Fecha de Nacimiento *"
                  error={errors.birthDate?.message}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Género *
                </label>
                <Select {...register("gender")} error={errors.gender?.message}>
                  <option value="">Seleccionar...</option>
                  <option value="male">Masculino</option>
                  <option value="female">Femenino</option>
                </Select>
              </div>
              <div>
                <Input
                  label="Email"
                  type="email"
                  {...register("email")}
                  error={errors.email?.message}
                  placeholder="correo@ejemplo.com"
                />
                {!errors.email &&
                  user?.email &&
                  watch("email")?.trim().toLowerCase() ===
                    user.email.toLowerCase() && (
                    <p className="text-amber-600 text-xs mt-1">
                      ⚠️ Este es tu propio correo. Si registras a otra persona,
                      déjalo vacío para que sea menor a tu cargo.
                    </p>
                  )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Teléfono *
                </label>
                <div className="flex gap-2">
                  <Select {...register("phoneCountryCode")} className="w-40">
                    {COUNTRY_CODES.map((c) => (
                      <option key={`${c.code}-${c.name}`} value={c.code}>
                        {c.flag} {c.code} {c.name}
                      </option>
                    ))}
                  </Select>
                  <Input
                    {...register("phone")}
                    error={errors.phone?.message}
                    placeholder="300 123 4567"
                    className="flex-1"
                  />
                </div>
              </div>
              <div className="md:col-span-2">
                <Input
                  label="Dirección"
                  {...register("address")}
                  placeholder="Calle 123 # 45-67"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Estado
                </label>
                <Select {...register("status")}>
                  <option value="active">Activo</option>
                  <option value="inactive">Inactivo</option>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Información Médica y Contacto de Emergencia */}
        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Heart className="w-5 h-5 text-red-500" />
              Información Médica y Contacto de Emergencia
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tipo de Sangre
                </label>
                <Select {...register("bloodType")}>
                  <option value="">Seleccionar...</option>
                  {bloodTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </Select>
              </div>
              <div>
                <EpsSelect
                  label="Seguro de Salud"
                  value={watch("healthInsurance") || ""}
                  onChange={(v) =>
                    setValue("healthInsurance", v, { shouldDirty: true })
                  }
                />
              </div>
              <div>
                <Input
                  label="Nombre Contacto Emergencia *"
                  {...register("emergencyContactName")}
                  error={errors.emergencyContactName?.message}
                  placeholder="María García"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Parentesco *
                </label>
                <Select
                  {...register("emergencyContactRelationship")}
                  error={errors.emergencyContactRelationship?.message}
                >
                  {relationships.map(({ value, label }) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Teléfono Emergencia *
                </label>
                <div className="flex gap-2">
                  <Select
                    {...register("emergencyContactCountryCode")}
                    className="w-40"
                  >
                    {COUNTRY_CODES.map((c) => (
                      <option key={`${c.code}-${c.name}`} value={c.code}>
                        {c.flag} {c.code} {c.name}
                      </option>
                    ))}
                  </Select>
                  <Input
                    {...register("emergencyContactPhone")}
                    error={errors.emergencyContactPhone?.message}
                    placeholder="300 123 4567"
                    className="flex-1"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Acciones */}
        <div className="flex justify-end gap-4">
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate("/members")}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            disabled={
              isSubmitting ||
              createMutation.isPending ||
              updateMutation.isPending
            }
            className="flex items-center gap-2"
          >
            {isSubmitting ||
            createMutation.isPending ||
            updateMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Guardando...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                {isEditing ? "Guardar Cambios" : "Crear Persona"}
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
